import type {KeyboardEvent, MouseEvent} from "react";
import {useLocation} from "react-router-dom";
import Check from "@mui/icons-material/Check";
import {useLanguageChoices} from "nav/useLanguageChoices";
import {languageAlternateUrls} from "nav/languageAlternates";
import {HREFLANG_TAGS} from "seo/siteMeta";
import {useSiteLinkOrigin} from "nav/useSiteLinkOrigin";
import {PanelItem} from "nav/hoverMenu";
import {Endonym, Flag} from "nav/LanguageRows.styles";

/**
 * The shared language ROWS (ui#117, nested under a "Language" parent item in ui#119): the flag rows
 * (🇬🇧 English / 🇷🇴 Română, endonym + a check on the active locale) — WITHOUT a heading, because the
 * "Language" label now lives on the parent {@link SubmenuItem} row that reveals these. Extracted from
 * the retired standalone globe language picker so every host of the language switch — since ui#812
 * that is the Resources hover-dropdown alone, in either auth state — renders the SAME rows through
 * the SAME {@link useLanguageChoices}/`useLocale` logic (ui#101), never a forked switch.
 *
 * **Each row is a real `<a href>` pointing at the other brand's copy of this page (#1545), and
 * activating it still switches language in place.** The two are not in conflict:
 *
 *  - The `href` is what makes the relationship between transferify.eu and transferify.ro a LINK.
 *    Before this, the only cross-brand statement in the markup was the `hreflang` pair, which passes
 *    no link value, so the .ro site — which has almost no external links — got nothing from the older
 *    property. It also makes "open in new tab" and "copy link address" land on the right page, which
 *    a click handler on a `<button>` cannot do.
 *  - Activation is still a language switch, not a navigation, because this control also lives in the
 *    signed-in app: a cross-origin jump would drop the reader's session and their place in it. So the
 *    click is prevented, the choice is persisted to localStorage `locale-v1`, and the app re-renders
 *    — exactly the behaviour ui#101 defined.
 *
 * **On a claimed or BYO customer host the rows stay plain buttons**, because there the anchor would
 * not link to another page of this site — it would send the customer's own recipient off their domain
 * onto ours. {@link languageAlternateUrls} owns that decision from the host answer; this component
 * only renders what it is given. The pre-render and the first client render have no host to read, so
 * the emitted HTML keeps its anchors and a claimed host drops them on mount.
 *
 * Space is handled explicitly: it activates a `<button>` but scrolls on an `<a>`, and this row was a
 * button until #1545. The optional `onSelected` fires first so the host menu (submenu + parent) can
 * close itself. Renders as flex-column children (the bare rows), so it drops straight into the flyout
 * {@link Panel} or the inline accordion.
 */
export default function LanguageRows({onSelected}: {onSelected?: () => void}) {
	const {choices, setLocale} = useLanguageChoices();
	const alternates = languageAlternateUrls(useLocation().pathname, useSiteLinkOrigin());

	return (
		<>
			{choices.map((choice) => {
				const select = () => {
					onSelected?.();
					setLocale(choice.code);
				};
				// A row with no alternate to offer is the control this was before #1545: same role, same
				// checked state, same handler — it simply carries no link off this host.
				// The anchor's `hreflang` is the same tag the page's `<link rel="alternate">` set claims
				// (ui#2471): these rows ARE that alternate, rendered as a link a reader can follow, so a second
				// spelling here would have the markup make two different claims about one URL.
				const linkProps = alternates === null
					? {type: "button" as const}
					: {as: "a" as const, href: alternates[choice.code], hrefLang: HREFLANG_TAGS[choice.code]};
				return (
					<PanelItem
						key={choice.code}
						{...linkProps}
						role="menuitemradio"
						aria-checked={choice.active}
						$selected={choice.active}
						onClick={(event: MouseEvent) => {
							event.preventDefault();
							select();
						}}
						onKeyDown={(event: KeyboardEvent) => {
							if (event.key !== " ") return;
							event.preventDefault();
							select();
						}}
					>
						<Flag aria-hidden="true">{choice.flag}</Flag>
						<Endonym>{choice.label}</Endonym>
						{choice.active && <Check fontSize="small" sx={{ml: "auto"}}/>}
					</PanelItem>
				);
			})}
		</>
	);
}
